/**
 * `PaymentReturn` — the member's navigational return view after a Paytrail
 * payment (044, US1 / FR-002a). Mounted at `/maksu/palaa`, which matches the
 * api redirect base (`${PAYTRAIL_REDIRECT_BASE_URL}/maksu/palaa`): the member's
 * browser lands here when Paytrail sends them back from the hosted page.
 *
 * This is a DISPLAY-ONLY return view. It shows one of two honest states, picked
 * from the `checkout-status` query param Paytrail appends to the return URL:
 *   - `ok` / `pending` / `delayed` → "payment received — confirmation pending"
 *     (the record confirms out-of-band via the S2S callback / redirect-verify
 *     path, US2 — this view never claims a settled payment on its own);
 *   - `fail`, or the param absent (a plain cancel return) → a NEUTRAL "payment
 *     not completed — try again" state (never a false "pending").
 *
 * It mints/consults NO session and takes no payment; the authoritative
 * confirmation + the disclosed "Maksettu (Paytrail)" label are US2/US3. Copy is
 * member-facing and Finnish-first (FR-011), under the `payment.return.*` scope.
 */

import type { JSX } from 'react';
import { Link, useSearchParams } from 'react-router';

import type { MessageKeys } from '../i18n/messages/index.js';
import { useTranslate } from '../i18n/useTranslate.js';
import { Card } from '../primitives/Card.js';

const pageClasses = 'flex flex-col gap-4';
const headingClasses = 'm-0 text-xl font-semibold';
const bodyClasses = 'm-0 text-base text-foreground';
const linkClasses = 'self-start text-sm font-medium text-primary underline';

// Paytrail's `checkout-status` values that mean "the provider accepted / is
// still settling the payment" — the member sees the pending-confirmation state.
// Everything else (`fail`, an unknown value, or an absent param on a plain
// cancel return) resolves to the neutral not-completed state.
const PENDING_STATUSES = new Set(['ok', 'pending', 'delayed']);

export function PaymentReturn(): JSX.Element {
  const t = useTranslate();
  const [params] = useSearchParams();
  const status = params.get('checkout-status');
  const pending = status !== null && PENDING_STATUSES.has(status);

  const headingKey: MessageKeys = pending
    ? 'payment.return.pending.heading'
    : 'payment.return.notCompleted.heading';
  const bodyKey: MessageKeys = pending
    ? 'payment.return.pending.body'
    : 'payment.return.notCompleted.body';

  return (
    <Card className={pageClasses}>
      <h2 className={headingClasses}>{t(headingKey)}</h2>
      <p className={bodyClasses} role="status">
        {t(bodyKey)}
      </p>
      <Link className={linkClasses} to="/profile">
        {t('payment.return.backToFee')}
      </Link>
    </Card>
  );
}
